//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * TEMPORARY Validation Polling Composable
 *
 * This is a TEMPORARY workaround until the backend sends full sample metadata via SignalR.
 *
 * Current Problem:
 * - Backend SignalR only sends barcode when new sample arrives
 * - Missing: sample type, color, priority, location, instrument, etc.
 * - Cannot display new samples properly without metadata
 *
 * Solution:
 * - Mark new samples with pulse indicator
 * - Poll backend every 5 minutes to refresh metadata
 * - Only poll when new samples exist (smart polling)
 * - Clear "new" indicator when user clicks sample
 *
 * **When to remove:**
 * - Once SignalR events include full sample metadata
 * - Update PendingResults.SampleCreated event to send complete sample object
 * - Then: Remove this composable entirely
 * - Remove polling setup from useValidationPage
 * - Remove isNewSample from Explorer props
 *
 * Responsibilities:
 * - Track "new" sample barcodes (arrived since last refresh)
 * - Pulse new samples for 10 seconds
 * - Schedule background refresh every 5 minutes
 * - Only poll when new samples exist (smart polling)
 * - Clear "new" indicators on user interaction
 *
 * @param key - Unique key for state isolation (default: "default")
 */
export const useValidationPolling = (key: string = "default") => {
  //
  // ============================================================================
  // CONFIGURATION
  // ============================================================================
  //

  // Get runtime configuration
  const config = useRuntimeConfig();

  //
  // ============================================================================
  // FEATURE FLAGS
  // ============================================================================
  //

  /**
   * Enable/disable polling globally
   *
   * Purpose:
   * - Allow disabling polling without code changes
   * - Quick toggle for testing without polling
   *
   * Configured via: NUXT_PUBLIC_POLLING_ENABLED (default: true)
   */
  const POLLING_ENABLED = config.public.polling.enabled;

  /**
   * Polling interval in milliseconds
   *
   * Purpose:
   * - How often to refresh sample metadata
   * - Balance between freshness and server load
   *
   * Configured via: NUXT_PUBLIC_POLLING_INTERVAL (default: 300000 / 5 minutes)
   */
  const POLLING_INTERVAL_MS = config.public.polling.interval;

  /**
   * Duration to pulse new samples (in milliseconds)
   *
   * Purpose:
   * - How long to show "new" indicator
   * - Auto-clear after this duration
   *
   * Configured via: NUXT_PUBLIC_POLLING_PULSE_DURATION (default: 10000 / 10 seconds)
   */
  const PULSE_DURATION_MS = config.public.polling.pulseDuration;

  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  /**
   * Set of barcodes marked as "new" (arrived since last refresh)
   *
   * Purpose:
   * - Track which samples need metadata refresh
   * - Drive pulse indicator in UI
   * - Determine if polling is needed
   */
  const newSampleBarcodes = useState<Set<string>>(`${key}.polling.newBarcodes`, () => new Set());

  /**
   * Map of barcode → timeout handle for auto-clearing pulse
   *
   * Purpose:
   * - Auto-clear "new" indicator after PULSE_DURATION_MS
   * - Cleanup timeouts when user clicks sample
   * - Prevent memory leaks
   */
  const pulseTimeouts = new Map<string, NodeJS.Timeout>();

  /**
   * Timestamp of last silent refresh
   *
   * Purpose:
   * - Track when last poll occurred
   * - Display "last refreshed" time to user
   */
  const lastRefreshTime = useState<number>(`${key}.polling.lastRefresh`, () => 0);

  /**
   * Next scheduled refresh time
   *
   * Purpose:
   * - Display countdown to user
   * - Show "next refresh in 4:32" feedback
   */
  const nextRefreshTime = useState<number>(`${key}.polling.nextRefresh`, () => 0);

  /**
   * Polling interval handle
   *
   * Purpose:
   * - Reference to setInterval for cleanup
   * - Stop polling on unmount
   */
  let pollingInterval: NodeJS.Timeout | null = null;

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  /**
   * Check if polling is needed
   *
   * Purpose:
   * - Smart polling: Only poll when new samples exist
   * - Save API calls when no new samples
   */
  const needsPolling = computed(() => {
    return POLLING_ENABLED && newSampleBarcodes.value.size > 0;
  });

  /**
   * Time until next refresh (in milliseconds)
   *
   * Purpose:
   * - Calculate countdown for UI display
   * - Used by nextRefreshIn computed
   *
   * Returns:
   * - 0 if polling not needed
   * - Milliseconds until next refresh
   */
  const timeUntilNextRefresh = computed(() => {
    if (!needsPolling.value) return 0;
    return Math.max(0, nextRefreshTime.value - Date.now());
  });

  /**
   * Time until next refresh (human-readable)
   *
   * Purpose:
   * - Format countdown for user feedback
   * - Display "Next refresh in 4:32"
   *
   * Format: "M:SS" (minutes:seconds)
   * Example: "4:32" = 4 minutes 32 seconds
   *
   * Returns:
   * - "" if polling not needed
   * - "M:SS" formatted string
   */
  const nextRefreshIn = computed(() => {
    if (!needsPolling.value) return "—";

    const ms = timeUntilNextRefresh.value;
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  });

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Mark a sample as new and start pulse timer
   *
   * Purpose:
   * - Add sample to "new" set (triggers pulse indicator)
   * - Start auto-clear timeout (10s duration)
   * - Trigger polling if not already running
   *
   * Responsibilities:
   * - Add barcode to newSampleBarcodes set
   * - Cancel existing timeout for this barcode (if any)
   * - Start new timeout to auto-clear after PULSE_DURATION_MS
   * - Timeout removes barcode from set (stops pulse)
   *
   * @param barcode - Sample barcode to mark as new
   */
  const markAsNew = (barcode: string): void => {
    // Clear any existing timeout for this barcode
    if (pulseTimeouts.has(barcode)) {
      clearTimeout(pulseTimeouts.get(barcode)!);
    }

    // Add to new samples set
    newSampleBarcodes.value.add(barcode);

    // Auto-clear after pulse duration
    const timeout = setTimeout(() => {
      newSampleBarcodes.value.delete(barcode);
      pulseTimeouts.delete(barcode);
    }, PULSE_DURATION_MS);

    pulseTimeouts.set(barcode, timeout);

    // If polling isn't running and we have new samples, start it
    if (!pollingInterval && POLLING_ENABLED) {
      console.log("[Polling] New sample detected, starting polling...");
    }
  };

  /**
   * Check if a sample is marked as new
   *
   * Purpose:
   * - Determine if sample should show pulse indicator
   * - Used by UI components to apply visual styling
   *
   * @param barcode - Sample barcode to check
   * @returns True if sample is in newSampleBarcodes set
   */
  const isNew = (barcode: string): boolean => {
    return newSampleBarcodes.value.has(barcode);
  };

  /**
   * Clear new indicator for a sample and cancel its pulse timer
   *
   * Purpose:
   * - Remove "new" indicator when user interacts with sample
   * - Cancel auto-clear timeout (user saw the sample)
   * - Stop polling if this was the last new sample
   *
   * Responsibilities:
   * - Remove barcode from newSampleBarcodes set
   * - Cancel pulse timeout for this barcode
   * - Delete timeout from pulseTimeouts map
   *
   * @param barcode - Sample barcode to clear
   */
  const clearNew = (barcode: string): void => {
    // Clear timeout
    if (pulseTimeouts.has(barcode)) {
      clearTimeout(pulseTimeouts.get(barcode)!);
      pulseTimeouts.delete(barcode);
    }

    // Remove from new samples set
    newSampleBarcodes.value.delete(barcode);
  };

  /**
   * Clear all new indicators and cancel all pulse timers
   *
   * Purpose:
   * - Reset all "new" indicators at once
   * - Cancel all auto-clear timeouts
   * - Stop polling immediately (no new samples left)
   *
   * Responsibilities:
   * - Cancel all pulse timeouts
   * - Clear pulseTimeouts map
   * - Clear newSampleBarcodes set
   */
  const clearAllNew = (): void => {
    // Clear all timeouts
    pulseTimeouts.forEach((timeout) => clearTimeout(timeout));
    pulseTimeouts.clear();

    // Clear all new indicators
    newSampleBarcodes.value.clear();
  };

  /**
   * Start smart polling (only when needed)
   *
   * Purpose:
   * - Schedule background refresh every 5 minutes
   * - Only poll when new samples exist (smart polling)
   * - Execute callback to refresh data
   *
   * Responsibilities:
   * - Check if polling already running (prevent duplicates)
   * - Set initial refresh timestamps
   * - Start 5-minute interval
   * - Check needsPolling before each refresh
   * - Stop automatically when no new samples
   *
   * @param onRefresh - Callback to execute on each refresh (async)
   */
  const startPolling = (onRefresh: () => Promise<void>): void => {
    if (!POLLING_ENABLED) {
      return;
    }

    // Clear existing interval
    stopPolling();

    // Set initial next refresh time
    nextRefreshTime.value = Date.now() + POLLING_INTERVAL_MS;
    lastRefreshTime.value = Date.now();

    // Start polling loop
    pollingInterval = setInterval(async () => {
      // SMART POLLING: Only refresh if there are new samples
      if (newSampleBarcodes.value.size === 0) {
        nextRefreshTime.value = Date.now() + POLLING_INTERVAL_MS;
        return;
      }

      try {
        await onRefresh();

        // Update timestamps
        lastRefreshTime.value = Date.now();
        nextRefreshTime.value = Date.now() + POLLING_INTERVAL_MS;

        // Clear new indicators after successful refresh
        clearAllNew();
      } catch (error) {
        // Silent polling error - continue with cached data
        // Don't clear new indicators on error - try again next interval
      }
    }, POLLING_INTERVAL_MS);
  };

  /**
   * Stop polling
   *
   * Purpose:
   * - Clean up interval on component unmount
   * - Stop background refresh when no longer needed
   *
   * Responsibilities:
   * - Clear polling interval
   * - Set pollingInterval to null
   */
  const stopPolling = (): void => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  /**
   * Trigger immediate refresh and reset timer
   *
   * Purpose:
   * - Allow manual refresh without waiting 5 minutes
   * - Reset polling timer after immediate refresh
   * - Used after barcode changes (TEMPORARY workaround)
   *
   * Responsibilities:
   * - Execute onRefresh callback immediately
   * - Update lastRefreshTime to now
   * - Reset nextRefreshTime to now + 5 minutes
   *
   * @param onRefresh - Callback to execute (async)
   */
  const refreshNow = async (onRefresh: () => Promise<void>): Promise<void> => {
    if (newSampleBarcodes.value.size === 0) {
      return;
    }

    await onRefresh();

    // Update timestamps
    lastRefreshTime.value = Date.now();
    nextRefreshTime.value = Date.now() + POLLING_INTERVAL_MS;

    // Clear new indicators
    clearAllNew();
  };

  // Cleanup on unmount
  onUnmounted(() => {
    stopPolling();
  });

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // State (readonly)
    newSampleBarcodes: readonly(newSampleBarcodes),
    lastRefreshTime: readonly(lastRefreshTime),
    nextRefreshTime: readonly(nextRefreshTime),

    // Computed
    needsPolling: readonly(needsPolling),
    timeUntilNextRefresh: readonly(timeUntilNextRefresh),
    nextRefreshIn: readonly(nextRefreshIn),

    // Methods
    markAsNew,
    isNew,
    clearNew,
    clearAllNew,
    startPolling,
    stopPolling,
    refreshNow,
  };
};
